import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Box,
  Chip,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Map as MapIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import {
  isMapsAvailable,
  getUsageStats,
  disableMaps,
  enableMaps,
  resetMonthlyQuota,
  getTotalUsage,
  clearQuotaData,
  MONTHLY_FREE_LIMITS,
  MONTHLY_LIMITS
} from '../../utils/googleMapsQuota';

const GoogleMapsSettings = () => {
  const [usageStats, setUsageStats] = useState(null);
  const [totalUsage, setTotalUsage] = useState(null);
  const [mapsEnabled, setMapsEnabled] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', title: '', message: '' });

  // Load usage data
  const loadUsageData = () => {
    const stats = getUsageStats();
    const total = getTotalUsage();
    const enabled = isMapsAvailable();
    
    setUsageStats(stats);
    setTotalUsage(total);
    setMapsEnabled(enabled);
  };

  useEffect(() => {
    loadUsageData();
    
    // Refresh every 30 seconds as a fallback
    const interval = setInterval(loadUsageData, 30000);

    // Refresh immediately when another tab writes to localStorage
    const handleStorageChange = (e) => {
      if (e.key === 'googleMapsQuota' || e.key === null) {
        loadUsageData();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Refresh immediately when the same tab updates quota
    window.addEventListener('googleMapsQuotaUpdated', loadUsageData);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('googleMapsQuotaUpdated', loadUsageData);
    };
  }, []);

  const handleDisableMaps = () => {
    setConfirmDialog({
      open: true,
      action: 'disable',
      title: 'Disable Google Maps',
      message: 'Are you sure you want to disable Google Maps? Users will be switched to manual address entry until you re-enable it.'
    });
  };

  const handleEnableMaps = () => {
    enableMaps();
    loadUsageData();
  };

  const handleResetMonthly = () => {
    setConfirmDialog({
      open: true,
      action: 'reset',
      title: 'Reset Monthly Quota',
      message: 'Are you sure you want to reset the monthly usage counters? This should only be done at the start of a new billing cycle.'
    });
  };

  const handleClearData = () => {
    setConfirmDialog({
      open: true,
      action: 'clear',
      title: 'Clear All Quota Data',
      message: 'Are you sure you want to clear all quota tracking data? This will reset all counters and re-enable Maps if disabled. Use this for troubleshooting only.'
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === 'disable') {
      disableMaps();
    } else if (confirmDialog.action === 'reset') {
      resetMonthlyQuota();
    } else if (confirmDialog.action === 'clear') {
      clearQuotaData();
    }
    
    setConfirmDialog({ open: false, action: '', title: '', message: '' });
    loadUsageData();
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };

  const getStatusChip = () => {
    if (!mapsEnabled) {
      return <Chip icon={<BlockIcon />} label="Disabled" color="error" />;
    }
    return <Chip icon={<CheckCircleIcon />} label="Enabled" color="success" />;
  };

  if (!usageStats || !totalUsage) {
    return <LinearProgress />;
  }

  return (
    <Card>
      <CardHeader
        avatar={<MapIcon />}
        title="Google Maps API Management"
        subheader="Monitor usage and control Google Maps functionality"
        action={getStatusChip()}
      />
      
      <CardContent>
        {/* Current Status Alert */}
        {!mapsEnabled && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Google Maps is currently disabled. Users will see manual address entry only.
            </Typography>
          </Alert>
        )}

        {/* Usage Warning */}
        {mapsEnabled && Object.values(usageStats).some(stat => stat.monthlyPercentage >= 80) && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
            <Typography variant="body2">
              <strong>High Usage Warning:</strong> You're approaching the 95% monthly limit (9,500 calls). 
              Consider monitoring usage closely or temporarily disabling Maps.
            </Typography>
          </Alert>
        )}

        {/* Control Buttons */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadUsageData}
          >
            Refresh Data
          </Button>
          
          {mapsEnabled ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={handleDisableMaps}
            >
              Disable Maps
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleEnableMaps}
            >
              Enable Maps
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="warning"
            onClick={handleResetMonthly}
          >
            Reset Monthly Counters
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearData}
          >
            Clear All Data
          </Button>
        </Box>

        {/* Usage Statistics */}
        <Grid container spacing={3}>
          {/* Monthly Usage */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Monthly Usage (Current Billing Cycle)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>API</strong></TableCell>
                    <TableCell align="right"><strong>Used</strong></TableCell>
                    <TableCell align="right"><strong>Limit (95%)</strong></TableCell>
                    <TableCell align="right"><strong>Free Tier</strong></TableCell>
                    <TableCell align="right"><strong>Usage %</strong></TableCell>
                    <TableCell><strong>Progress</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(usageStats).filter(([key]) => key !== 'disabled' && key !== 'resetsAt').map(([api, stats]) => (
                    <TableRow key={api}>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {api === 'maps' ? 'Maps JavaScript API' : 
                           api === 'places' ? 'Places API (Autocomplete)' : 
                           'Geocoding API'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {stats.monthlyUsed?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {stats.monthlyLimit?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {MONTHLY_FREE_LIMITS[api]?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${stats.monthlyPercentage || 0}%`}
                          color={getUsageColor(stats.monthlyPercentage || 0)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(stats.monthlyPercentage || 0, 100)}
                            color={getUsageColor(stats.monthlyPercentage || 0)}
                            sx={{ width: 100, height: 6 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Daily Usage */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Today's Usage
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>API</strong></TableCell>
                    <TableCell align="right"><strong>Used Today</strong></TableCell>
                    <TableCell align="right"><strong>Daily Limit</strong></TableCell>
                    <TableCell align="right"><strong>Usage %</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(usageStats).filter(([key]) => key !== 'disabled' && key !== 'resetsAt').map(([api, stats]) => (
                    <TableRow key={api}>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {api === 'maps' ? 'Maps JavaScript API' : 
                           api === 'places' ? 'Places API (Autocomplete)' : 
                           'Geocoding API'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {stats.used?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {stats.limit?.toLocaleString() || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${stats.percentage || 0}%`}
                          color={getUsageColor(stats.percentage || 0)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Info Panel */}
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Free Tier Limits:</strong> 10,000 calls per month per API. 
                Our system uses 95% (9,500) as the safe limit to avoid charges.
                {usageStats.resetsAt && (
                  <><br /><strong>Counters reset:</strong> {usageStats.resetsAt}</>
                )}
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: '', title: '', message: '' })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', title: '', message: '' })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default GoogleMapsSettings;
